import { Injectable, inject } from '@angular/core';
import { AdditionalInfoStore } from '../stores/additional-info.store';
import { AdditionalInfo, GroupedAdditionalInfo } from '../models/classes/additionalinfo';

@Injectable({
  providedIn: 'root',
})
export class AdditionalInfoService {
  private additionalInfoStore = inject(AdditionalInfoStore);

  readonly isLoading = this.additionalInfoStore.loading;
  readonly infoTypes = this.additionalInfoStore.infoTypes;
  readonly defaultFields = this.additionalInfoStore.defaultFields;
  readonly currentObjectId = this.additionalInfoStore.currentObjectId;
  readonly groupedInfo = this.additionalInfoStore.groupedInfo;
  readonly hasInfoTypes = this.additionalInfoStore.hasInfoTypes;
  readonly ultrasonicCheckDefaults = this.additionalInfoStore.ultrasonicCheckDefaults;
  readonly rotatingEquipmentDefaults = this.additionalInfoStore.rotatingEquipmentDefaults;

  /**
   * Load additional info for an object
   * @param objectID - The object ID to load info for
   * @param equipmentType - The equipment type (flange, pipe, steamtrap, etc.)
   * @returns Promise with grouped additional info
   */
  async loadAdditionalInfo(
    objectID: number,
    equipmentType: string
  ): Promise<GroupedAdditionalInfo[]> {
    return this.additionalInfoStore.loadAdditionalInfo(objectID, equipmentType);
  }

  /**
   * Save additional info group
   * @param infoGroup - The info group to save
   * @param objectID - The object ID
   * @param equipmentType - The equipment type
   * @returns Promise with updated grouped info
   */
  async saveAdditionalInfo(
    infoGroup: GroupedAdditionalInfo,
    objectID: number,
    equipmentType: string
  ): Promise<GroupedAdditionalInfo[]> {
    return this.additionalInfoStore.saveAdditionalInfo(infoGroup, objectID, equipmentType);
  }

  /**
   * Revert changes to cached values
   * @param objectID - The object ID
   * @param equipmentType - The equipment type
   * @returns Grouped additional info from cache
   */
  revertChanges(objectID: number, equipmentType: string): GroupedAdditionalInfo[] {
    return this.additionalInfoStore.revertChanges(objectID, equipmentType);
  }

  /**
   * Get ultrasonic check defaults for a measurement system
   * @param measurementSystem - The measurement system (mt, im, etc.)
   * @returns Array of default additional info fields
   */
  getUltrasonicCheckDefaults(measurementSystem: string): AdditionalInfo[] {
    // Use the computed signal but map to proper type with measurement system
    return this.additionalInfoStore.ultrasonicCheckDefaults().map((f) => ({
      ...f,
      currentMeasurementSystem: measurementSystem,
    })) as AdditionalInfo[];
  }

  /**
   * Get rotating equipment defaults for a measurement system
   * @param measurementSystem - The measurement system (mt, im, etc.)
   * @returns Array of default additional info fields
   */
  getRotatingEquipmentDefaults(measurementSystem: string): AdditionalInfo[] {
    // Use the computed signal but map to proper type with measurement system
    return this.additionalInfoStore.rotatingEquipmentDefaults().map((f) => ({
      ...f,
      currentMeasurementSystem: measurementSystem,
    })) as AdditionalInfo[];
  }

  /**
   * Clear all state
   */
  clear(): void {
    this.additionalInfoStore.clear();
  }

  /**
   * Clear cache for a specific object or all cache
   * @param objectID - Optional object ID to clear specific cache
   */
  clearCache(objectID?: number): void {
    this.additionalInfoStore.clearCache(objectID);
  }

  /**
   * Load info types if not already loaded
   */
  async ensureInfoTypesLoaded(): Promise<void> {
    if (!this.hasInfoTypes()) {
      await this.additionalInfoStore.loadInfoTypes();
    }
  }
}
